import React, { useState } from 'react';
import './App.css';
import HeroSection from './components/HeroSection';
import PainPointsSection from './components/PainPointsSection';
import ServicesSection from './components/ServicesSection';
import ContactForm from './components/ContactForm';
import ChatBot from './components/ChatBot';
import { Mail, Bot, MessageSquare } from 'lucide-react';
import { Button } from './components/ui/button';

function App() {
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Scroll to bottom if contact section isn't rendered yet
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  return (
    <div className="App" style={{ background: 'var(--bg-page)' }}>
      {/* Header - Transparent to blend with hero */}
      <header 
        className="header-nav"
        style={{
          background: 'transparent',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50
        }}
      >
        <div className="container mx-auto flex items-center justify-between">
          <video 
            autoPlay
            loop
            muted
            playsInline
            className="logo"
            style={{ height: '48px', width: 'auto' }}
          >
            <source src="https://customer-assets.emergentagent.com/job_1cbf181c-f588-4bee-a281-c5df8c1cd653/artifacts/m38cf5um_Silver%20dollar%20logo.mp4" type="video/mp4" />
          </video>
          <nav className="nav-actions">
            <button 
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              className="btn-secondary hover-scale"
              style={{ padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.9)', color: '#2D2D2D' }}
            >
              Services
            </button>
            <button 
              onClick={scrollToContact}
              className="btn-primary hover-scale"
              style={{ padding: '0.5rem 1.2rem', background: '#2D2D2D' }}
            >
              <span className="flex items-center gap-2">
                <Mail size={16} />
                Contact
              </span>
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <HeroSection onGetStarted={scrollToContact} />

      {/* Pain Points Section - Real Problems We Solve */}
      <PainPointsSection />

      {/* Services Section */}
      <div id="services">
        <ServicesSection />
      </div>

      {/* MTD Navigator Section */}
      <section className="py-16 px-4" style={{ background: '#2D2D2D' }}>
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="heading-1 mb-4" style={{ color: '#D4AF37' }}>The "MTD" Service Wash</h2>
            <p className="heading-3 mb-4" style={{ color: '#C41E3A' }}>Don't get caught in the spin cycle.</p>
            <p className="body-large" style={{ color: '#C0C0C0', maxWidth: '700px', margin: '0 auto' }}>
              From April 2026, HMRC is changing the rules. If you earn over £50k, the "shoebox" is dead. You need digital records, quarterly updates, and zero stress.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            <div 
              className="p-6 text-center"
              style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                border: '2px solid #D4AF37'
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧺</div>
              <h3 className="heading-3 mb-3" style={{ color: '#D4AF37' }}>The Sort</h3>
              <p className="body-medium" style={{ color: '#A0A0A0' }}>
                We move your messy paper trail into an automated AI system (like Dext or Sage Copilot).
              </p>
            </div>
            
            <div 
              className="p-6 text-center"
              style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                border: '2px solid #4CAF50'
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧼</div>
              <h3 className="heading-3 mb-3" style={{ color: '#4CAF50' }}>The Wash</h3>
              <p className="body-medium" style={{ color: '#A0A0A0' }}>
                Our AI "Navigator" automatically categorizes your expenses every single day.
              </p>
            </div>
            
            <div 
              className="p-6 text-center"
              style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                border: '2px solid #C41E3A'
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✨</div>
              <h3 className="heading-3 mb-3" style={{ color: '#C41E3A' }}>The Press</h3>
              <p className="body-medium" style={{ color: '#A0A0A0' }}>
                We handle the mandatory HMRC quarterly updates for you. No more January panics.
              </p>
            </div>
          </div>

          <div 
            className="mt-10 p-6 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)',
              borderRadius: '1rem',
              border: '1px solid #D4AF37'
            }}
          >
            <p className="body-large" style={{ color: '#FFFFFF', fontWeight: '600', marginBottom: '0.5rem' }}>
              The Zing Factor
            </p>
            <p className="body-medium" style={{ color: '#D4AF37', fontStyle: 'italic' }}>
              "Our fees are 100% tax-deductible. Not only does the AI save you 30+ hours of admin a year, but the taxman effectively pays for a chunk of the service. It's the only 'tax' you'll actually enjoy paying."
            </p>
          </div>
        </div>
      </section>

      {/* USP Section - The t-AI-t Difference */}
      <section className="py-20 px-4" style={{ background: '#2D2D2D' }}>
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4" style={{ color: '#FFFFFF' }}>Why Us? The Two-Man Advantage</h2>
            <p className="body-large" style={{ color: '#C0C0C0', maxWidth: '700px', margin: '0 auto' }}>
              You're not talking to a faceless algorithm. You're talking to Brendan and the team — real people who actually care about getting it right.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div 
              className="p-8"
              style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                border: '2px solid #4CAF50'
              }}
            >
              <h3 className="heading-3 mb-4" style={{ color: '#4CAF50' }}>Zero Friction</h3>
              <p className="body-medium" style={{ color: '#C0C0C0' }}>
                No jargon. No courses. No learning curves. You bring the ideas, we hand back the finished "laundry". That's it.
              </p>
            </div>
            
            <div 
              className="p-8"
              style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                border: '2px solid #D4AF37'
              }}
            >
              <h3 className="heading-3 mb-4" style={{ color: '#D4AF37' }}>50/50 Peace of Mind</h3>
              <p className="body-medium" style={{ color: '#C0C0C0' }}>
                Pay 50% to start, 50% only when you're delighted. No subscriptions, no lock-ins, no "tech-bro" nonsense. Just honest work.
              </p>
            </div>
            
            <div 
              className="p-8"
              style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                border: '2px solid #C41E3A'
              }}
            >
              <h3 className="heading-3 mb-4" style={{ color: '#C41E3A' }}>Human-Verified Results</h3>
              <p className="body-medium" style={{ color: '#C0C0C0' }}>
                Every piece of work is checked by real eyes before it reaches you. AI does the heavy lifting, humans ensure the quality.
              </p>
            </div>
            
            <div 
              className="p-8"
              style={{
                background: '#1a1a1a',
                borderRadius: '1rem',
                border: '2px solid #C0C0C0'
              }}
            >
              <h3 className="heading-3 mb-4" style={{ color: '#C0C0C0' }}>Your Data Stays Yours</h3>
              <p className="body-medium" style={{ color: '#A0A0A0' }}>
                We never train AI on your information. Full UK data protection compliance. What you share stays between us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Middle Slogan Banner */}
      <section 
        className="py-12 px-4"
        style={{
          background: 'linear-gradient(135deg, #2D2D2D 0%, #1a1a1a 100%)',
          textAlign: 'center'
        }}
      >
        <p style={{ 
          fontSize: '2rem', 
          fontWeight: '700', 
          color: '#D4AF37',
          fontStyle: 'italic',
          margin: 0
        }}>
          "We do your AI. You do your life."
        </p>
      </section>

      {/* How We Work Section */}
      <section className="py-20 px-4" style={{ background: '#1a1a1a' }}>
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="heading-1 mb-6" style={{ color: '#FFFFFF' }}>How It Works</h2>
          <p className="body-large mb-12" style={{ color: '#C0C0C0', maxWidth: '700px', margin: '0 auto 1.5rem' }}>
            Like dropping off your laundry. You hand us the problem, we hand back the solution.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            <div className="p-6">
              <div 
                className="mx-auto mb-4"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '3px solid #000000',
                  overflow: 'hidden',
                  padding: '8px'
                }}
              >
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/e098f008-dbf7-471e-80f0-9e58c066e5fe/images/665d0cb821ecfd8cba732009dca3ac97c53576e7f4b7838c5ea57fe9247f0200.png" 
                  alt="Brendan"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <h3 className="heading-3 mb-3" style={{ color: '#FFFFFF' }}>Chat with Brendan</h3>
              <p className="body-small" style={{ color: '#A0A0A0' }}>
                Tell him what you need. No tech-speak required — just plain English about your problem.
              </p>
            </div>
            
            <div className="p-6">
              <div 
                className="mx-auto mb-4"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  border: '3px solid #D4AF37',
                  fontWeight: 'bold',
                  color: '#FFFFFF'
                }}
              >
                2
              </div>
              <h3 className="heading-3 mb-3" style={{ color: '#FFFFFF' }}>We Get to Work</h3>
              <p className="body-small" style={{ color: '#A0A0A0' }}>
                Pay 50% and we crack on. You don't need to learn anything or do any homework.
              </p>
            </div>
            
            <div className="p-6">
              <div 
                className="mx-auto mb-4"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #C41E3A 0%, #8B0000 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  border: '3px solid #D4AF37',
                  fontWeight: 'bold',
                  color: '#FFFFFF'
                }}
              >
                3
              </div>
              <h3 className="heading-3 mb-3" style={{ color: '#FFFFFF' }}>We Hand It Back</h3>
              <p className="body-small" style={{ color: '#A0A0A0' }}>
                Your finished work, explained in plain English. No manuals, no confusion.
              </p>
            </div>
            
            <div className="p-6">
              <div 
                className="mx-auto mb-4"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  border: '3px solid #000000',
                  fontWeight: 'bold',
                  color: '#000000'
                }}
              >
                4
              </div>
              <h3 className="heading-3 mb-3" style={{ color: '#FFFFFF' }}>You're Delighted</h3>
              <p className="body-small" style={{ color: '#A0A0A0' }}>
                Happy? Pay the other 50%. Not happy? We fix it until you are. Simple.
              </p>
            </div>
          </div>

          <div 
            className="mt-12 p-8"
            style={{
              background: '#2D2D2D',
              borderRadius: '1rem',
              border: '2px solid #D4AF37',
              maxWidth: '700px',
              margin: '3rem auto 0'
            }}
          >
            <p className="body-large" style={{ color: '#D4AF37', fontWeight: '600', marginBottom: '0.5rem' }}>
              The Service Wash Promise
            </p>
            <p className="body-medium" style={{ color: '#FFFFFF' }}>
              You drop off the problem. We hand back the result. That's it.
            </p>
          </div>
        </div>
      </section>

      {/* Brendan CTA Section */}
      <section className="py-20 px-4" style={{ 
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(196, 30, 58, 0.9) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 70%)',
            zIndex: 0
          }}
        />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <div 
            className="inline-block mb-6"
            style={{
              width: '180px',
              height: '180px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '5px solid #FFFFFF',
              boxShadow: '0 12px 48px rgba(212, 175, 55, 0.5)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            >
              <source src="https://customer-assets.emergentagent.com/job_ai-advisor-tait/artifacts/qwgftd4m_lv_7588133859170979089_20260218194808.mp4" type="video/mp4" />
            </video>
          </div>
          
          <h2 className="heading-1 mb-4" style={{ color: '#FFFFFF' }}>Meet Brendan</h2>
          <p className="body-large mb-4" style={{ color: '#FFFFFF', maxWidth: '700px', margin: '0 auto 1rem', fontStyle: 'italic' }}>
            "He's the only bot you'll ever have to talk to. He does the translating so we can do the work."
          </p>
          <p className="body-medium mb-8" style={{ color: 'rgba(255, 255, 255, 0.8)', maxWidth: '600px', margin: '0 auto 2rem' }}>
            Brendan is available 24/7 to understand your needs in plain English. 
            No tech jargon, no complicated forms - just tell him what's been eating up your time.
          </p>
          
          <Button 
            onClick={() => {
              // Scroll to bottom and trigger chat
              window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
              // Small delay to ensure scroll happens first
              setTimeout(() => {
                const chatButton = document.querySelector('button[aria-label="Chat with Brendan"]');
                if (chatButton) chatButton.click();
              }, 500);
            }}
            className="btn-primary hover-scale"
            style={{ 
              minWidth: '200px',
              background: '#D4AF37',
              color: '#000000',
              border: '2px solid #FFFFFF',
              fontSize: '1rem',
              padding: '1rem 2rem'
            }}
          >
            <span className="flex items-center gap-2">
              <MessageSquare size={20} />
              Chat with Brendan Now
            </span>
          </Button>
        </div>
      </section>

      {/* Tax Breaks Section */}
      <section className="py-16 px-4" style={{ background: '#2D2D2D' }}>
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="heading-2 mb-4" style={{ color: '#D4AF37' }}>Could This Be Tax Deductible?</h2>
          <p className="body-large mb-6" style={{ color: '#FFFFFF' }}>
            If you're a UK business, our services may qualify as allowable business expenses — reducing your tax bill while you invest in efficiency.
          </p>
          <div 
            className="p-6 text-left"
            style={{
              background: '#1a1a1a',
              borderRadius: '1rem',
              border: '1px solid #D4AF37'
            }}
          >
            <p className="body-medium mb-4" style={{ color: '#C0C0C0', fontWeight: '600' }}>Potentially claimable:</p>
            <ul style={{ color: '#A0A0A0', lineHeight: '2' }}>
              <li>✓ AI automation setup <span style={{ color: '#888' }}>(operational costs)</span></li>
              <li>✓ Website & landing page development <span style={{ color: '#888' }}>(marketing/advertising)</span></li>
              <li>✓ Content creation services <span style={{ color: '#888' }}>(marketing)</span></li>
              <li>✓ Business process consulting <span style={{ color: '#888' }}>(professional services)</span></li>
            </ul>
          </div>
          <p className="body-small mt-6" style={{ color: '#888', fontStyle: 'italic' }}>
            Always check with your accountant, but many of our clients find that investing in AI pays twice — once in time saved, again at tax time.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4" style={{ background: '#1a1a1a' }}>
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4" style={{ color: '#FFFFFF' }}>Let's Work Together</h2>
            <p className="body-large" style={{ color: '#C0C0C0' }}>
              Tell us about your project, and we'll get back to you within 48 hours.
            </p>
          </div>
          <div 
            className="p-8"
            style={{
              background: '#2D2D2D',
              borderRadius: '1rem',
              border: '1px solid #D4AF37',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
            }}
          >
            <ContactForm />
          </div>
        </div>
      </section>

      {/* End Slogan Banner */}
      <section 
        className="py-16 px-4"
        style={{
          background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.95) 0%, rgba(212, 175, 55, 0.9) 50%, rgba(192, 192, 192, 0.95) 100%)',
          textAlign: 'center'
        }}
      >
        <p style={{ 
          fontSize: '2.5rem', 
          fontWeight: '700', 
          color: '#2D2D2D',
          fontStyle: 'italic',
          margin: 0,
          textShadow: '1px 1px 2px rgba(255,255,255,0.3)'
        }}>
          "We do your AI. You do your life."
        </p>
      </section>

      {/* Footer */}
      <footer 
        className="py-12 px-4 border-t"
        style={{
          background: 'var(--bg-card)',
          borderColor: 'var(--border-light)'
        }}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <video 
                autoPlay
                loop
                muted
                playsInline
                style={{ height: '48px', width: 'auto', marginBottom: '1rem' }}
              >
                <source src="https://customer-assets.emergentagent.com/job_1cbf181c-f588-4bee-a281-c5df8c1cd653/artifacts/m38cf5um_Silver%20dollar%20logo.mp4" type="video/mp4" />
              </video>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                Oxford & Superhuman AI certified
              </p>
              <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                Perth and Kinross, Scotland
              </p>
            </div>
            <div>
              <h4 className="heading-3 mb-4">Core Services</h4>
              <ul className="space-y-2">
                <li><a href="#services" className="body-small hover:underline" style={{ color: 'var(--text-secondary)' }}>The Digital Ghostwriter</a></li>
                <li><a href="#services" className="body-small hover:underline" style={{ color: 'var(--text-secondary)' }}>The Information Sieve</a></li>
                <li><a href="#services" className="body-small hover:underline" style={{ color: 'var(--text-secondary)' }}>The AI Operations Audit</a></li>
                <li><a href="#services" className="body-small hover:underline" style={{ color: 'var(--text-secondary)' }}>Landing Pages & Websites</a></li>
                <li><a href="#services" className="body-small hover:underline" style={{ color: 'var(--text-secondary)' }}>AI Strategy Consulting</a></li>
                <li><a href="#services" className="body-small hover:underline" style={{ color: 'var(--text-secondary)' }}>Creative & Design</a></li>
              </ul>
            </div>
            <div>
              <h4 className="heading-3 mb-4">Contact</h4>
              <p className="body-small mb-3" style={{ color: 'var(--text-secondary)' }}>
                Email: brendan@t-ai-t.com
              </p>
              <a 
                href="mailto:brendan@t-ai-t.com?subject=MTD%20%26%20AI%20Readiness%20Audit%20Request"
                className="inline-block mt-3 px-4 py-2"
                style={{
                  background: '#D4AF37',
                  color: '#000000',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  fontSize: '0.85rem',
                  textDecoration: 'none'
                }}
              >
                Request an MTD & AI Readiness Audit
              </a>
              <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '1rem', fontStyle: 'italic' }}>
                We master the AI so you can be the master of your day.
              </p>
            </div>
          </div>
          <div 
            className="pt-8 border-t text-center"
            style={{ borderColor: 'var(--border-light)' }}
          >
            <p className="caption" style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
              © 2026 t AI t consultancy. All rights reserved.
            </p>
            <p className="caption" style={{ color: 'var(--text-muted)', maxWidth: '700px', margin: '0 auto', fontSize: '0.7rem', lineHeight: '1.5', marginBottom: '1rem' }}>
              t-AI-t is an AI automation consultancy, not a firm of Chartered Accountants. While our systems are designed to make you MTD-ready and our fees are generally 100% tax-deductible as a business expense, we always recommend a "Quick Spin" with your personal accountant to ensure our solutions fit your specific tax profile.
            </p>
            
            {/* AI Disclaimer */}
            <div 
              style={{ 
                maxWidth: '700px', 
                margin: '1rem auto 0', 
                padding: '1rem',
                background: 'rgba(212, 175, 55, 0.1)',
                borderRadius: '0.5rem',
                border: '1px solid rgba(212, 175, 55, 0.3)'
              }}
            >
              <p className="caption" style={{ color: '#D4AF37', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.75rem' }}>
                AI Usage & Accuracy Notice
              </p>
              <p className="caption" style={{ color: 'var(--text-muted)', fontSize: '0.65rem', lineHeight: '1.6', marginBottom: '0.5rem' }}>
                Content on t-ai-t.com is generated by Artificial Intelligence (Claude by Anthropic). While we strive for high-quality insights, AI-generated outputs are for informational and creative purposes only.
              </p>
              <p className="caption" style={{ color: 'var(--text-muted)', fontSize: '0.65rem', lineHeight: '1.6' }}>
                <strong>No Professional Advice:</strong> t-ai-t.com does not provide legal, financial, medical, or professional advice. 
                <strong> User Responsibility:</strong> You are responsible for verifying any information before acting upon it. 
                <strong> Liability:</strong> We are not liable for any inaccuracies, errors, or outcomes resulting from the use of AI-generated content.
              </p>
              <p className="caption" style={{ color: '#A0A0A0', fontSize: '0.6rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                By using this site, you acknowledge that you are interacting with an LLM and agree to use the service responsibly.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Chatbot */}
      <ChatBot />
    </div>
  );
}

export default App;